import { Link, useNavigate } from "react-router-dom"; // Added useNavigate
import React, { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import TawkToChat from "../components/TawkToChat";
import { analytics, db, Timestamp } from "../firebase-config"; // Keep db, Timestamp, collection, addDoc for error logging
// import useDateUtils from "../utils/fromStringToTimestamp"; // No longer directly needed if handled by hook/elsewhere
import { useToast } from "../components/Alerts";
import {
  collection,
  addDoc,
} from "firebase/firestore";
import { useAuth } from "../useAuth";
import { ColorRing } from "react-loader-spinner";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
// import DialogflowMessenger from "../components/DialogflowMessenger";
import CreateJobModal from "../components/createJobModal";
import Navbar from "../components/Navbar";
import StatusModal from "../components/StatusModal";
import JobPostingActions from "../components/JobPostingActions";
// import { sanitizeInput } from "../utils/inputSanitizer"; // Moved to hook
import JobPostingDescription from "../components/JobPostingDescription";
import { useSelector, useDispatch } from "react-redux";
import LicenseRequiredModal from "../components/LicenseRequiredModal";
import {
  fetchJobPostings,
  selectJobPostings,
  selectJobPostingsError,
  loadMoreJobPostings, // Keep if using pagination
  selectJobPostingsLoading,
  // clearJobPostings, // Optional: consider clearing on mount/unmount if needed
} from "../utils/redux/jobPostingsSlice";
import {
  // selectAllUsers,
  selectUserByEmail,
  fetchUserByEmail,
} from "../utils/redux/userDataSlice";
import {
  fetchAndSetCompanyData,
  selectAllCompanys,
} from "../utils/redux/companySlice";

import {
  fetchAssessments,
  selectAssessments,
} from "../utils/redux/assessmentSlice";
// Import the custom hook
import { useCreateJob } from "../hooks/useCreateJob"; // Adjust path as needed

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
// const PROJECT_ID = process.env.REACT_APP_PROJECT_ID;

const JobPostingsPage = () => {
  const { t, i18n } = useTranslation("jobpostingpage"); // Add i18n if needed for language setting
  const { currentUser } = useAuth();
  const assessments = useSelector(selectAssessments);
  // const { stringToTimestamp } = useDateUtils(); // Moved to hook
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Use navigate for redirects
  const userData = useSelector((state) =>
    currentUser ? selectUserByEmail(state, currentUser.email) : null
  );
  const companyData = useSelector(selectAllCompanys); // Array
  const jobPostings = useSelector(selectJobPostings);
  const loading = useSelector(selectJobPostingsLoading); // This is likely for fetching jobs
  // const [disableButtons, setDisableButtons] = useState(false); // Replaced by hook's isLoading
  const error = useSelector(selectJobPostingsError); // Error from fetching jobs
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [hasAnyLicense, setHasAnyLicense] = useState(false); // Keep for modal logic
  const [basicLicenseCount, setBasicLicenseCount] = useState(0);
  const [growthLicenseCount, setGrowthLicenseCount] = useState(0); // Added
  const [proLicenseCount, setProLicenseCount] = useState(0); // 'pro' for Professional
  const [premiumLicenseCount, setPremiumLicenseCount] = useState(0); // Added
  const [currentJobPosting, setCurrentJobPosting] = useState(null); // Initialize as null
  const [newJobData, setNewJobData] = useState({}); // Keep for modal form state
  // const [pageURL, setPageURL] = useState(window.location.href); // Get URL directly when needed
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const { addToast } = useToast(); // Keep for potential non-hook toasts

  // --- Helper function for logging errors to Firestore ---
  const logErrorToFirestore = async (error, context = "Job Creation") => {
    try {
      await addDoc(collection(db, "prorecruit_error"), {
        page: `JobPostingsPage (${context})`, // Provide context
        data: {
          companyName: companyData?.[0]?.name || "N/A", // Safe access
          tenantId: userData?.tenantId || "N/A",
          email: userData?.email || "N/A",
          uuid: userData?.uuid || "N/A",
          displayName: userData?.displayName || "N/A",
        },
        url: window.location.href,
        message: error?.message || "Unknown error",
        stack: error?.stack || "No stack trace",
        name: error?.name || "Error",
        timestamp: Timestamp.now(), // Use Timestamp.now()
      });
    } catch (firestoreError) {
      console.error("Error saving error log to Firestore:", firestoreError);
      if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
        Sentry.captureException(firestoreError, {
          extra: { context: "Failed to log original error to Firestore" },
        });
      }
    }
  };

  // --- Use the custom hook for job creation ---
  const { createJob, isLoading: isCreatingJob } = useCreateJob({
    currentUser,
    userData,
    companyData, // Pass the array
    // eslint-disable-next-line no-unused-vars
    onSuccess: (createdJob) => {
      // Success: close modal, reset form, re-fetch data
      setIsCreateModalOpen(false);
      setNewJobData({});
      if (userData?.tenantId) {
        dispatch(fetchJobPostings({ tenantId: userData.tenantId })); // Re-fetch jobs
        dispatch(fetchAndSetCompanyData(userData.tenantId)); // Re-fetch company data (for licenses)
        dispatch(fetchAssessments(userData.tenantId));
      }
      // Hook shows its own success toast, add another only if needed:
      // addToast({ type: "success", message: t("jobCreatedSuccessSpecific") });
    },
    onError: (error) => {
      // Error: log it specifically for this page context
      console.error("Job creation failed (Hook Error Callback):", error);
      logErrorToFirestore(error, "Job Creation Hook");
      // Hook shows its own error toast, add another only if needed:
      // addToast({ type: "error", message: t("errorCreatingJobPostingSpecific") });
    },
  });
  // --- End hook usage ---

  useEffect(() => {
    // Set language based on browser preference initially
    const browserLang = navigator.language.split("-")[0];
    if (i18n.language !== browserLang && ["en", "fr"].includes(browserLang)) {
      // Add supported langs
      i18n.changeLanguage(browserLang);
    }
    // Set title dynamically based on translation
    document.title = t("jobPostingsTitle") + " - ProRecruit";
    // Cleanup function
    return () => {
      document.title = "ProRecruit"; // Reset to a default title
      // Optional: Clear job postings from redux state on unmount?
      // dispatch(clearJobPostings());
    };
  }, [t, i18n]); // Re-run if language changes

  // Effect for initial data fetching and license check
  useEffect(() => {
    let isMounted = true; // Prevent state updates on unmounted component
    const fetchData = async () => {
      if (currentUser?.email && isMounted) {
        // Set user context for analytics
        setUserId(analytics, currentUser.uid);
        setUserProperties(analytics, {
          fullName: currentUser.displayName || "unknown",
          email: currentUser.email,
        });

        try {
          // Fetch user data first to get tenantId
          const userAction = await dispatch(
            fetchUserByEmail(currentUser.email)
          );
          if (
            fetchUserByEmail.fulfilled.match(userAction) &&
            userAction.payload?.tenantId
          ) {
            const tenantId = userAction.payload.tenantId;

            // Fetch company data and job postings concurrently
            const [companyAction] = await Promise.all([
              dispatch(fetchAndSetCompanyData(tenantId)),
              dispatch(fetchJobPostings({ tenantId })), // Fetch initial jobs
            ]);

            // Check licenses after company data is fetched
            if (
              fetchAndSetCompanyData.fulfilled.match(companyAction) &&
              companyAction.payload &&
              companyAction.payload.length > 0
            ) {
              const currentCompany = companyAction.payload[0];
              setHasAnyLicense(
                (currentCompany.proLicense ?? 0) +
                  (currentCompany.basicLicense ?? 0) +
                  (currentCompany.trialLicense ?? 0) >
                  0 || currentCompany.hasUsedFreeTrial === false // Adjust logic as needed
              );
            } else {
              setHasAnyLicense(false); // Default if company data fails
            }
          } else {
            console.error(
              "Failed to fetch user data or tenantId for initial load."
            );
            addToast({ type: "error", message: t("errorFetchingUserData") });
            setHasAnyLicense(false);
          }
        } catch (error) {
          console.error("Error during initial data fetch:", error);
          if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
            Sentry.captureException(error, {
              extra: { context: "Initial Data Fetch JobPostingsPage" },
            });
          }
          addToast({ type: "error", message: t("errorFetchingInitialData") });
          setHasAnyLicense(false);
        }
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    }; // Cleanup
  }, [currentUser, dispatch, addToast, t]); // Dependencies

  // Effect to update local license counts and hasAnyLicense when companyData changes
  useEffect(() => {
    if (companyData && companyData.length > 0) {
      const currentCompany = companyData[0];
      // ** Update state for all 4 license types **
      setBasicLicenseCount(currentCompany.basicLicense ?? 0);
      setGrowthLicenseCount(currentCompany.growthLicense ?? 0); // Assuming field name 'growthLicense'
      setProLicenseCount(currentCompany.proLicense ?? 0);
      setPremiumLicenseCount(currentCompany.premiumLicense ?? 0); // Assuming field name 'premiumLicense'

      // ** Update hasAnyLicense check to include all types **
      setHasAnyLicense(
        (currentCompany.basicLicense ?? 0) +
        (currentCompany.growthLicense ?? 0) + // Added Growth
        (currentCompany.proLicense ?? 0) +
        (currentCompany.premiumLicense ?? 0) + // Added Premium
          // Keep trial logic as needed
          (currentCompany.trialLicense ?? 0) >
          0 || currentCompany.hasUsedFreeTrial === false
      );
    } else {
      // Reset if company data is not available
      setBasicLicenseCount(0);
      setGrowthLicenseCount(0); // Reset Growth
      setProLicenseCount(0);
      setPremiumLicenseCount(0); // Reset Premium
      setHasAnyLicense(false);
    }
  }, [companyData]); // Re-run when companyData updates
  // Removed redundant useEffect for fetching job postings based on userData change

  // Removed showSuccessToast / showErrorToast - Handled by hook or addToast directly

  // Keep this handler for the modal's form inputs
  const handleCreateJobChange = (e) => {
    if (e && e.target) {
      setNewJobData({ ...newJobData, [e.target.name]: e.target.value });
    } else if (Array.isArray(e) || (e && e.value !== undefined)) {
      // Handle react-select more robustly
      const fieldName = "assessments"; // Assume 'assessments', adjust if needed
      setNewJobData((prevData) => ({
        ...prevData,
        [fieldName]: e, // Store raw value/values from react-select
      }));
    }
  };

  const handleGetLicense = () => {
    navigate("/checkout"); // Use navigate
  };

  const handleLearnMore = () => {
    navigate("/pricing"); // Use navigate
  };

  // --- generateShortCode function removed (handled by hook) ---

  const handleOpenStatusModal = (posting) => {
    logEvent(analytics, "select_content", {
      content_type: "ClickChangeStatus",
      content_id: posting.uuid,
    });
    setCurrentJobPosting(posting);
    setIsStatusModalOpen(true);
  };

  // Keep this handler as it uses a specific API endpoint/logic not in the create hook
  const handleUpdateJobPostingStatus = async (newStatus) => {
    // uuid comes from currentJobPosting
    if (!currentJobPosting || !userData?.tenantId) {
      console.error("Missing job posting or tenant ID for status update.");
      addToast({ type: "error", message: t("errorUpdatingStatusMissingInfo") });
      return;
    }

    logEvent(analytics, "select_content", {
      content_type: "ChangedJobPostingStatus",
      content_id: `${currentJobPosting.uuid}_${newStatus}`,
    });

    const updatedJobPosting = { ...currentJobPosting, Statut: newStatus };

    try {
      // Consider adding loading state for this specific action
      const response = await fetch(
        `${API_ENDPOINT}/api/v1/tenants/${userData.tenantId}/jobpostings/${currentJobPosting.uuid}`, // Use template literal
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            // Add Authorization header if required by your API
            // "Authorization": `Bearer ${currentUser?.getIdToken()}`
          },
          body: JSON.stringify(updatedJobPosting),
        }
      );

      if (!response.ok) {
        // Handle API error response
        const errorData = await response.text(); // or response.json() if applicable
        throw new Error(
          `API Error (${response.status}): ${errorData || response.statusText}`
        );
      }

      // If status moves to Completed or Canceled, trigger the Cloud Task
      if (newStatus === "Completer" || newStatus === "Annuler") {
        const payload = {
          documentId: currentJobPosting.uuid,
          tenantId: userData.tenantId,
          status: newStatus, // Use the newStatus directly
          tenantName: currentJobPosting.Entreprise,
          recruiterName: userData.displayName,
          recruiterEmail: userData.email,
          jobTitle: currentJobPosting.Titre,
          fromCollectionName: "active", // Source collection
          collectionName: newStatus === "Completer" ? "completed" : "canceled", // Target collection
        };

        const taskResponse = await fetch(`${API_ENDPOINT}/api/v1/tasks`, {
          // Use template literal
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Add Authorization header if required by your API
          },
          body: JSON.stringify({
            // Ensure your task endpoint expects this structure
            url: `${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}/cancel-job-descriptions`, // Use template literal, ensure env var is set
            payload: payload,
          }),
        });

        if (!taskResponse.ok) {
          const taskErrorData = await taskResponse.text();
          // Log this error but don't necessarily block UI update
          console.error(
            `Cloud Task API Error (${taskResponse.status}): ${
              taskErrorData || taskResponse.statusText
            }`
          );
          Sentry.captureMessage(
            "Cloud Task submission failed for job status change",
            { extra: { payload, error: taskErrorData } }
          );
          addToast({
            type: "warning",
            message: t("warningTaskCreationFailed"),
          });
        }
      }

      addToast({ type: "success", message: t("statusUpdateSuccess") });
      setIsStatusModalOpen(false);
      // Re-fetch job postings to reflect the status change immediately
      dispatch(fetchJobPostings({ tenantId: userData.tenantId }));
    } catch (error) {
      console.error("Error updating job posting status:", error);
      if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
        Sentry.captureException(error, {
          extra: { context: "Update Job Posting Status" },
        });
      }
      logErrorToFirestore(error, "Update Job Status");
      addToast({
        type: "error",
        message: `${t("errorUpdatingStatus")}: ${error.message}`,
      });
      // Optionally keep modal open on error? setIsStatusModalOpen(false);
    }
  };

  const toggleCreateModal = () => {
    logEvent(analytics, "select_content", {
      content_type: "ClickCreateJobPosting",
      content_id: "New",
    });
    // Use the state derived from the initial fetch
    if (hasAnyLicense) {
      setIsCreateModalOpen(!isCreateModalOpen);
    } else {
      setIsLicenseModalOpen(true);
    }
  };

  // --- handleCreateJobSubmit removed (replaced by `createJob` from hook) ---

  // --- Render logic ---

  // Loading state (for initial job fetch)
  if (loading && jobPostings.length === 0) {
    // Show full screen loader only on initial load
    return (
      <div className="flex justify-center items-center h-screen">
        <ColorRing
          visible={true}
          height="80"
          width="80"
          ariaLabel="loading-indicator"
          wrapperStyle={{}}
          wrapperClass="blocks-wrapper"
          colors={["#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE", "#EFF6FF"]} // Example blue theme
        />
      </div>
    );
  }

  // Error state (for initial job fetch)
  if (error) {
    return (
      <>
        <Navbar userData={userData} /> {/* Show navbar even on error */}
        <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)] text-center px-4">
          {" "}
          {/* Adjusted height */}
          <h1 className="text-3xl font-bold text-red-600 mb-4">
            {t("errorTitle", "Error Loading Data")} {/* Use translation */}
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            {t(
              "errorLoadingMessage",
              "Could not load job postings. Please try again later."
            )}{" "}
            {/* Use translation */}
          </p>
          <pre className="text-sm text-slate-500 bg-gray-100 p-2 rounded max-w-md overflow-auto">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </pre>
          {/* Optionally add a retry button */}
          <button
            onClick={() => {
              if (userData?.tenantId)
                dispatch(fetchJobPostings({ tenantId: userData.tenantId }));
            }}
            className="mt-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-[#A47713] hover:bg-[#8a6310]"
          >
            {t("retry", "Retry")}
          </button>
        </div>
      </>
    );
  }

  // Main content render
  return (
    <>
      <Navbar
        onCreateJob={toggleCreateModal} // Pass the function to open the create modal
        userData={userData}
        // Add other props as needed, e.g., active link highlighting
      />

      {/* Modals */}
      {isLicenseModalOpen && (
        <LicenseRequiredModal
          isOpen={isLicenseModalOpen}
          onClose={() => setIsLicenseModalOpen(false)}
          onGetLicense={handleGetLicense}
          onLearnMore={handleLearnMore}
        />
      )}

      <CreateJobModal
        isOpen={isCreateModalOpen}
        onClose={toggleCreateModal}
        onSubmit={createJob}
        onChange={handleCreateJobChange}
        data={newJobData}
        disableButtons={isCreatingJob}
        assessments={assessments}
        basicLicenseCount={basicLicenseCount}
        growthLicenseCount={growthLicenseCount} // Pass growth count
        proLicenseCount={proLicenseCount}
        premiumLicenseCount={premiumLicenseCount} // Pass premium count
        hasUsedFreeTrial={companyData[0]?.hasUsedFreeTrial ?? true}
      />

      <StatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        currentJobPosting={currentJobPosting} // Pass the selected job
        onUpdateStatus={handleUpdateJobPostingStatus} // Pass the update handler
      />

      {/* Main Page Content */}
      <div className="container mx-auto p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-bold tracking-tight">
            {t("jobPostingsTitle")} ({jobPostings.length})
          </h1>
          {/* Optional: Add create job button here as well/instead of Navbar */}
          <button
            onClick={toggleCreateModal}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-semibold transition duration-150 ease-in-out"
          >
            {t("createJobButton", "Create New Job")}
          </button>
        </div>

        {/* Job Postings List */}
        {jobPostings.length > 0 ? (
          <ul className="space-y-6">
            {" "}
            {/* Use space-y for consistent spacing */}
            {jobPostings.map((
              posting // Removed index usage
            ) => (
              <li
                key={posting.uuid || posting.JobID} // Prefer uuid if available
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200" // Added border
              >
                <div className="p-4 sm:p-6">
                  {" "}
                  {/* Added padding */}
                  <div className="flex flex-col sm:flex-row justify-between items-start mb-2">
                    <Link
                      to={`/jobpostings/${posting.uuid}/tenants/${posting.tenantId}`}
                      className="text-xl font-semibold text-blue-600 hover:underline mb-1 sm:mb-0"
                    >
                      {posting.Titre || t("untitledJob", "Untitled Job")} -{" "}
                      {posting.JobID}
                    </Link>
                    {/* Status Badge */}
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        posting.Statut === "Offres"
                          ? "bg-green-100 text-green-800"
                          : posting.Statut === "Submissions"
                          ? "bg-blue-100 text-blue-800"
                          : posting.Statut === "Annuler"
                          ? "bg-red-100 text-red-800"
                          : posting.Statut === "Completer"
                          ? "bg-gray-100 text-slate-900 font-bold tracking-tight"
                          : "bg-yellow-100 text-yellow-800" // Default/other status
                      }`}
                    >
                      {posting.Statut || t("unknownStatus", "Unknown")}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-1">
                    {posting.Entreprise ||
                      t("unknownCompany", "Unknown Company")}
                    , {posting.Lieu || t("unknownLocation", "Unknown Location")}
                  </p>
                  {/* Apply Link */}
                  <a
                    className="text-sm text-blue-500 hover:underline mb-2 block" // Made block for spacing
                    href={
                      posting.linkToApply
                        ? posting.linkToApply // Use the direct link if available
                        : `${
                            process.env.REACT_APP_BASE_URL ||
                            window.location.origin
                          }/jobs/${posting.JobID}` // Fallback construction
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${t("applyLinkLabel", "View or Apply for")} ${
                      posting.Titre
                    }`}
                  >
                    {t("applyLink", "View/Apply Link")}{" "}
                    <span aria-hidden="true">→</span>
                  </a>
                  {/* Deadline */}
                  <p
                    className={
                      posting.DateLimiteDeCandidature + 86400000 - 1 >
                      Timestamp.now().toMillis()
                        ? "text-slate-500"
                        : "text-red-600"
                    }
                  >
                    {t("applicationDeadline", "Deadline")}:{" "}
                    {new Date(
                      posting.DateLimiteDeCandidature
                    ).toLocaleDateString()}
                  </p>
                  {/* Candidates Link */}
                  <Link
                    to={`/candidates/${posting.uuid}/tenants/${posting.tenantId}`}
                    state={{ userData, posting }} // Pass necessary state
                    className="text-sm text-blue-600 hover:underline mb-3 block" // Block for spacing
                  >
                    {t("viewCandidates", "View Candidates")} (
                    {posting.numberOfApplicants || 0})
                  </Link>
                  {/* Job Description Snippet/Component */}
                  <JobPostingDescription
                    description={posting.Description}
                    maxLength={150}
                  />{" "}
                  {/* Optional: Add maxLength prop */}
                  {/* Action Buttons */}
                  <JobPostingActions
                    posting={posting}
                    userData={userData}
                    onOpenStatusModal={handleOpenStatusModal} // Pass status modal opener
                    // Pass other actions like edit, delete if needed
                  />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          // Message when no job postings are found
          <div className="text-center py-10">
            <p className="text-slate-600 leading-relaxed text-lg">
              {t("noJobPostingsFound", "No active job postings found.")}
            </p>
            <button
              onClick={toggleCreateModal}
              className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-semibold transition duration-150 ease-in-out"
            >
              {t("createFirstJobButton", "Create Your First Job Posting")}
            </button>
          </div>
        )}

        {/* Load More Button - Only show if there might be more */}
        {jobPostings.length > 0 && ( // Check if jobPostings exist before showing load more
          <div className="flex justify-center mt-8">
            <button
              onClick={() => {
                if (userData?.tenantId) {
                  dispatch(
                    loadMoreJobPostings({ tenantId: userData.tenantId })
                  );
                }
              }}
              className="px-5 py-2 bg-blue-500 text-white rounded hover:bg-[#A47713] hover:bg-[#8a6310] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading} // Disable while loading more
            >
              {loading
                ? t("loading", "Loading...")
                : t("loadMore", "Load More")}
            </button>
          </div>
        )}
      </div>

      <TawkToChat />

      {/* Conditionally render Dialogflow if needed */}
      {/* {showChatbot && userData?.uuid && (
        <DialogflowMessenger
          userId={userData.uuid}
          userParams={{ name: userData.displayName }}
        />
      )} */}
    </>
  );
};

export default JobPostingsPage;
